import * as React from "react";
import { useNavigate } from "react-router-dom";
import { useTranslations } from "next-intl";
import { useMe, useOrg, usePendingApplicants } from "@chotca/api-client";
import { Logo } from "@chotca/ui";
import { AuthGate } from "@/components/AuthGate";

export function WelcomePage() {
  return (
    <AuthGate role="EMPLOYER">{(me) => <WelcomeInner me={me} />}</AuthGate>
  );
}

function WelcomeInner({
  me,
}: {
  me: NonNullable<ReturnType<typeof useMe>["data"]>;
}) {
  const t = useTranslations("welcome");
  const nav = useNavigate();
  const orgId = me.memberships[0]?.orgId;
  const org = useOrg(orgId);
  const pending = usePendingApplicants(1);

  const name = me.employerProfile?.fullName?.trim() ?? "";
  const orgName = org.data?.name ?? me.memberships[0]?.orgName ?? "";
  const applicants = pending.data?.totalCount ?? 0;
  const credits = org.data?.inviteCreditsAvailable ?? 0;

  const steps: StepProps[] = [
    {
      n: 1,
      active: true,
      title: t("step1Title"),
      desc:
        applicants > 0
          ? t("step1Desc", { count: applicants })
          : t("step1DescEmpty"),
      onClick: () => nav("/applicants"),
    },
    {
      n: 2,
      title: t("step2Title"),
      desc: t("step2Desc"),
      onClick: () => nav("/applicants"),
    },
    {
      n: 3,
      title: t("step3Title"),
      badge: t("step3Badge"),
      desc: t("step3Desc"),
      onClick: () => nav("/org/kyc"),
    },
  ];

  return (
    <main className="flex min-h-screen w-full items-center justify-center bg-mist p-4 sm:p-6">
      <div className="w-full max-w-md lg:max-w-4xl">
        <div className="overflow-hidden rounded-3xl border border-smoke bg-paper shadow-[0_30px_80px_-30px_rgba(0,0,0,0.3)] lg:grid lg:grid-cols-[1.05fr_0.95fr]">
          {/* ── Greeting pane (dark) ─────────────────────────────── */}
          <div className="relative overflow-hidden bg-[radial-gradient(130%_120%_at_0%_0%,#16402f_0%,#0f0f0f_60%)] p-6 text-white lg:p-8">
            <span
              aria-hidden
              className="pointer-events-none absolute -right-16 -top-16 h-52 w-52 rounded-full bg-lime/20 blur-2xl"
            />
            <div className="relative flex h-full flex-col">
              <Logo variant="horizontal" size={30} onDark />

              <span className="mt-8 flex h-14 w-14 items-center justify-center rounded-2xl bg-lime text-2xl text-emerald-950 shadow-[0_10px_24px_-8px_rgba(50,255,170,0.7)]">
                ✓
              </span>
              <h1 className="mt-4 text-[22px] font-extrabold leading-snug tracking-tight lg:text-[26px]">
                {name ? t("greeting", { name }) : "Chào mừng!"}
                <br />
                {t("managingNow", { orgName })}
              </h1>
              <p className="mt-3 text-sm leading-relaxed text-white/60">
                {t("subtitle")}
              </p>

              <div className="mt-6 flex gap-2.5">
                <Stat value={applicants} label={t("statApplicants")} />
                <Stat value={credits} label={t("statCredits")} lime />
              </div>
            </div>
          </div>

          {/* ── Checklist pane (light) ───────────────────────────── */}
          <div className="flex flex-col p-6 lg:p-8">
            <div className="text-xs font-bold uppercase tracking-wide text-ash">
              {t("stepsLabel")}
            </div>

            <div className="mt-3 space-y-3">
              {steps.map((s) => (
                <Step key={s.n} {...s} />
              ))}
            </div>

            <div className="mt-6 flex flex-col gap-2.5 lg:mt-auto lg:pt-6">
              <button
                onClick={() => nav("/applicants")}
                className="w-full rounded-xl bg-lime px-4 py-3.5 text-[15px] font-bold text-emerald-950 shadow-[0_10px_24px_-10px_rgba(20,163,110,0.8)]"
              >
                {t("ctaPrimary")} →
              </button>
              <button
                onClick={() => nav("/dashboard")}
                className="w-full py-1 text-sm font-semibold text-ash"
              >
                {t("ctaSecondary")}
              </button>
            </div>
          </div>
        </div>
      </div>
    </main>
  );
}

function Stat({
  value,
  label,
  lime,
}: {
  value: number;
  label: string;
  lime?: boolean;
}) {
  return (
    <div className="flex-1 rounded-2xl border border-white/10 bg-white/6 px-3.5 py-3">
      <div
        className={
          "text-2xl font-extrabold leading-none " +
          (lime ? "text-lime" : "text-white")
        }
      >
        {value}
      </div>
      <div className="mt-1.5 text-xs text-white/55">{label}</div>
    </div>
  );
}

type StepProps = {
  n: number;
  title: string;
  desc: string;
  badge?: string;
  active?: boolean;
  onClick?: () => void;
};

function Step({ n, title, desc, badge, active, onClick }: StepProps) {
  return (
    <button
      onClick={onClick}
      className={
        "flex w-full items-center gap-3.5 rounded-2xl border bg-paper p-4 text-left " +
        (active ? "border-lime-400 ring-3 ring-lime-400/20" : "border-smoke")
      }
    >
      <span
        className={
          "flex h-7 w-7 flex-none items-center justify-center rounded-full text-sm font-bold " +
          (active ? "bg-lime-400 text-emerald-950" : "bg-[#f1f1f2] text-ash")
        }
      >
        {n}
      </span>
      <div>
        <b className="text-[15px] text-ink">
          {title}
          {badge && (
            <span className="ml-1.5 rounded-full border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-[11px] font-bold text-emerald-600">
              {badge}
            </span>
          )}
        </b>
        <div className="mt-0.5 text-[13px] text-ash">{desc}</div>
      </div>
      <span className="ml-auto text-lg text-[#c4c4c9]">›</span>
    </button>
  );
}
